/**
 * NotInReportNotesPanel — an amber-tinted notes panel used for review content
 * that the clinician records for their own reasoning but which is deliberately
 * NOT fed into AI drafting or the final patient report.
 *
 * Two sections on the Adult Autism review pages use this treatment:
 *   • "Emerging Formulation"      — the clinician's working synthesis.
 *   • "Confidential Case Notes"   — private case notes.
 *
 * The amber colouring plus a small "Not included in final report" caption in
 * the bottom-right (italic, low-emphasis — matching the Emerging Differentials
 * panel and the ADHD review pages) tells the clinician at a glance that anything
 * typed here stays internal.
 *
 * It is a thin, controlled wrapper around StructuredNotesEditor: the parent owns
 * the notes array and receives changes via `onNotesChange`. The `domainId` must
 * be unique per panel instance on a page so the editor's drag-and-drop / move
 * machinery can address each notes box independently.
 *
 * `allowDragWhileReadOnly` makes the panel a read-only drag *source* — note
 * cards show grips and can be dragged into an editable destination elsewhere on
 * the page (e.g. Final Formulation). Drops are still rejected while `canEdit`
 * is false; this prop only unlocks the source side. Defaults to false so panels
 * on pages without a drop target (MDT Review, SSI/OBS Review) stay inert.
 */

import React from 'react';
import StructuredNotesEditor from '../../pages/providers/12_observation/components/StructuredNotesEditor';

export default function NotInReportNotesPanel({
  title,
  description,
  notes,
  onNotesChange,
  canEdit = true,
  domainId,
  placeholder = 'Type a note and press Enter…',
  rows = 6,
  className = '',
  onMoveNoteIn,
  allowDragWhileReadOnly = false,
}) {
  return (
    <div className={`rounded-2xl border border-amber-200 bg-amber-50 px-5 pt-5 pb-2 flex flex-col ${className}`}>
      <h3 className="text-sm font-bold text-gray-800 mb-0.5">{title}</h3>
      {description && (
        <p className="text-xs text-gray-500 mb-2">{description}</p>
      )}
      <StructuredNotesEditor
        notes={notes}
        onChange={onNotesChange}
        placeholder={placeholder}
        rows={rows}
        domainId={domainId}
        onMoveNoteIn={onMoveNoteIn}
        readOnly={!canEdit}
        allowDragWhileReadOnly={allowDragWhileReadOnly}
      />
      <p className="text-xs italic text-amber-900/80 text-right mt-1 mb-0">
        Not included in final report
      </p>
    </div>
  );
}
